import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'telegram-stars-refunds-21-day-hold',
  title: 'Telegram Stars Refunds, the 21-Day Hold and Payout Rules Every Seller Should Know',
  metaTitle: 'Telegram Stars Refunds & 21-Day Hold: Payout Rules Explained',
  metaDescription:
    'Why Telegram holds Stars for 21 days, how refunds work for sellers and buyers, minimum payout amounts, and how to manage cash flow when selling with Telegram Stars.',
  keywords: [
    'telegram stars refund',
    'telegram stars 21 days',
    'telegram stars hold period',
    'telegram stars payout',
    'telegram stars withdrawal rules',
    'refund telegram stars payment',
  ],
  category: 'Business',
  datePublished: '2026-10-03',
  dateModified: '2026-10-12',
  readingTimeMin: 6,
  excerpt:
    'Two rules surprise new Stars sellers: payments are locked for 21 days, and refunds return Stars rather than money. This article explains both, the minimum payout amounts, and how to plan your cash flow around them.',
  keyTakeaways: [
    'Every Stars payment becomes withdrawable 21 days after it is received; the hold protects buyers and lets Telegram process refunds and fraud checks.',
    'Sellers can refund any payment in full with refundStarPayment (or from a platform dashboard); buyers receive Stars back, not cash.',
    'Minimum payout is 1,000 Stars on Fragment and 100 Stars on Telegram Stars Payment Platform; payouts are made in TON.',
    'Plan cash flow as a rolling 21-day pipeline: steady daily sales produce steady daily availability after the first three weeks.',
  ],
  body: [
    h2('The 21-day holding period'),
    p(
      'When a user pays you in Stars, the amount appears in your balance immediately but is **not withdrawable for 21 days**. The rule is set by Telegram and applies to all sellers: bots, channels, Mini Apps and payment platforms. It exists for three reasons:',
    ),
    ul([
      '**Refund window**: gives sellers and Telegram time to process legitimate refunds before funds leave the system.',
      '**Fraud prevention**: stolen cards and abusive purchases are usually detected within this window.',
      '**App store compliance**: in-app purchases can be reversed by Apple or Google; the hold aligns Stars payouts with that reality.',
    ]),
    p(
      'Telegram Stars Payment Platform reflects the rule in your ledger: each payment carries an **availability date**, your dashboard shows a *maturing* balance and an *available* balance, and withdrawal requests can only draw on the available part.',
    ),

    h2('How to think about cash flow'),
    p(
      'The hold is not a one-time delay, it is a pipeline. If you sell every day, after three weeks you also unlock funds every day. The practical consequences:',
    ),
    ol([
      'Expect your **first payout about three weeks after your first sale**.',
      'From then on, withdraw on a fixed schedule (weekly or monthly) rather than chasing each matured payment.',
      'Keep prices and campaigns steady; spiky sales mean spiky availability.',
      'If you need working capital for the first month, treat Stars as receivables, not cash.',
    ]),

    h2('Refunds: how they work'),
    p(
      'Any Stars payment can be refunded in full by the seller. Through the Bot API the call is `refundStarPayment(user_id, telegram_payment_charge_id)`; on a payment platform it is a button in the dashboard. Key facts:',
    ),
    ul([
      'The buyer receives the **Stars** back in their Telegram balance, not dollars. They can spend them elsewhere.',
      'Refunds are **full** (the entire payment); partial refunds are not supported.',
      'You can refund **after delivery**; it is a seller decision, so publish a refund policy.',
      'Refunded Stars are deducted from your balance, maturing first if they had not yet matured.',
      'Telegram may also refund on its own in cases of fraud or store-level chargebacks, which is exactly what the 21-day hold is for.',
    ]),
    callout('Clear product descriptions and instant delivery are the two most effective ways to keep refund rates low. See our tips in [How to sell digital products with Stars](/blog/sell-digital-products-telegram-stars).'),

    h2('Subscriptions and refunds'),
    p(
      'Stars subscriptions renew every 30 days. Users can cancel at any time from their Telegram settings; the current period stays active until it expires. Sellers can cancel or restore a subscription with `editUserStarSubscription`. A refund of a subscription payment follows the same rules as a one-off payment.',
    ),

    h2('Minimum payout amounts'),
    table(
      ['Where you withdraw', 'Minimum', 'Payout asset', 'Notes'],
      [
        ['Fragment (bot or channel balance)', '1,000 Stars', 'TON', 'Instant on-chain once matured'],
        ['Telegram Stars Payment Platform', '100 Stars', 'TON', 'Reviewed then paid; 3% fee'],
        ['Telegram Ads (spend instead of withdraw)', 'Varies', 'Ad credit', 'Often a better rate for growth'],
      ],
    ),
    p('Step-by-step instructions are in [How to withdraw Telegram Stars to TON](/blog/withdraw-telegram-stars-to-ton).'),

    h2('Record keeping'),
    p('Whether for taxes or disputes, keep for each payment: date, amount in Stars, payer reference, invoice/payload, the `telegram_payment_charge_id`, and for each payout the on-chain transaction reference. A platform ledger does this automatically; with your own bot, store it in your database and export monthly.'),

    h2('Frequently misunderstood points'),
    ul([
      '**"My balance shows X but I can only withdraw Y"**: the difference is Stars younger than 21 days or reserved by pending withdrawal requests.',
      '**"The buyer asked for a card refund"**: Stars refunds return Stars. The buyer can spend them on any Telegram product; cash refunds are not possible for Stars.',
      '**"Can I speed up the hold?"**: no, the 21 days are enforced by Telegram for everyone.',
      '**"Does the 3% fee apply to refunded payments?"**: on Telegram Stars Payment Platform the fee is applied at withdrawal on what you actually withdraw, so refunded Stars are not charged.',
    ]),
  ],
  faq: [
    {
      q: 'Why are my Telegram Stars locked for 21 days?',
      a: 'Telegram holds every Stars payment for 21 days to allow refunds, fraud checks and app store reversals. The rule applies to all sellers.',
    },
    {
      q: 'Can a buyer get money back for a Telegram Stars purchase?',
      a: 'Sellers can refund the Stars in full, which return to the buyer\'s Telegram balance. Stars cannot be converted back to cash by users.',
    },
    {
      q: 'Can I partially refund a Stars payment?',
      a: 'No. refundStarPayment refunds the entire payment. For partial compensation, issue a new free product or a discount.',
    },
    {
      q: 'What is the minimum payout for Telegram Stars?',
      a: '1,000 Stars on Fragment, 100 Stars on Telegram Stars Payment Platform. Both pay out in TON after the 21-day hold.',
    },
  ],
  relatedSlugs: [
    'withdraw-telegram-stars-to-ton',
    'telegram-stars-fees-explained',
    'telegram-stars-bot-api-developer-guide',
  ],
};

export default article;
